import {
  AT_HOME_SITUATION,
  HOME_CARE_SITUATION,
  LIVING_SITUATION_ALONE,
  LIVING_SITUATION_CAREHOME,
  LIVING_SITUATION_IN_ELDERLY_HOME,
  LIVING_SITUATION_SHARED_FLAT,
  LIVING_SITUATION_WITH_FAMILY,
  PREDICAMENT_STATE_NO,
  REHAB_FORM_NAMES,
} from "core/consts";
import { Auction, GetOntologyType, PredicamentState } from "core/types";
import Translations from "translations/types";
import { RehabFormFormGetters } from "../configs";
import { auctionBirthdatetoUnix } from "../utils";
import { GUARDIAN_1, GUARDIAN_2 } from "core/schemas/GKVGeneralFormSchema";

const getGuardian = (auction: Auction, guardianIndex: number) => {
  const guardians = auction.patient?.profile?.communication?.guardians;
  return Array.isArray(guardians) && guardians.length > guardianIndex
    ? guardians[guardianIndex]
    : null;
};

const GUARDIAN_FIELD_MAPPINGS = [
  "first_name",
  "last_name",
  "phone",
  "street",
  "house_number",
  "zipcode",
  "city",
  "type",
] as const;

const createGuardianGetters = (
  guardianNumber: typeof GUARDIAN_1 | typeof GUARDIAN_2,
) => {
  const guardianIndex = guardianNumber - 1;

  const fieldGetters = GUARDIAN_FIELD_MAPPINGS.reduce<
    Record<string, (auction: Auction) => string | number | null>
  >((acc, fieldName) => {
    const formFieldName =
      `insured_details_guardian_${guardianNumber}_${fieldName}` as const;
    acc[formFieldName] = (auction: Auction) =>
      fieldName === "type"
        ? getGuardian(auction, guardianIndex)?.[fieldName] ?? null
        : getGuardian(auction, guardianIndex)?.[fieldName]?.decrypted ?? null;
    return acc;
  }, {});

  return fieldGetters;
};

export const importAuctionToGKVGeneralForm = (
  _getOntology: GetOntologyType,
  _translations: Translations,
): RehabFormFormGetters<typeof REHAB_FORM_NAMES.GKV_GENERAL> => ({
  case_id: (auction) => auction.patient.external_id ?? null,
  communication_in_german_possible: (auction) => {
    if (
      auction.patient.profile?.communication
        ?.patient_cannot_communicate_in_german
    ) {
      return PREDICAMENT_STATE_NO;
    }
    return null;
  },
  communication_other_language: (auction) =>
    auction.patient.profile?.communication?.patient_language || null,
  domestic_situation_is_safe: (auction) =>
    auction.patient.profile?.unsafe_current_domestic_situation_state ?? null,
  general_practitioner_city: (auction) =>
    auction.patient.profile?.general_practitioner?.city?.decrypted ?? null,
  general_practitioner_contact_details: (auction) =>
    auction.patient.profile?.general_practitioner?.contact_details ?? null,
  general_practitioner_first_name: (auction) =>
    auction.patient.profile?.general_practitioner?.first_name?.decrypted ??
    null,
  general_practitioner_honorific_prefix: (auction) =>
    auction.patient.profile?.general_practitioner?.honorific_prefix
      ?.decrypted ?? null,
  general_practitioner_honorific_suffix: (auction) =>
    auction.patient.profile?.general_practitioner?.honorific_suffix
      ?.decrypted ?? null,
  general_practitioner_housenumber: (auction) =>
    auction.patient.profile?.general_practitioner?.house_number?.decrypted ??
    null,
  general_practitioner_job_title: (auction) =>
    auction.patient.profile?.general_practitioner?.job_title?.decrypted ?? null,
  general_practitioner_last_name: (auction) =>
    auction.patient.profile?.general_practitioner?.last_name?.decrypted ?? null,
  general_practitioner_name: (auction) =>
    auction.patient.profile?.general_practitioner?.name ?? null,
  general_practitioner_phone_number: (auction) =>
    auction.patient.profile?.general_practitioner?.phone?.decrypted ?? null,
  general_practitioner_street: (auction) =>
    auction.patient.profile?.general_practitioner?.street?.decrypted ?? null,
  general_practitioner_zipcode: (auction) =>
    auction.patient.profile?.general_practitioner?.zipcode?.decrypted ?? null,
  hospital_city: (auction) =>
    auction.patient.careseeker?.address?.city ?? null,
  hospital_house_number: (auction) =>
    auction.patient.careseeker?.address?.street_number ?? null,
  hospital_name: (auction) => auction.patient.careseeker?.name ?? null,
  hospital_postal_code: (auction) =>
    auction.patient.careseeker?.address?.zipcode ?? null,
  hospital_street: (auction) =>
    auction.patient.careseeker?.address?.street ?? null,
  insurance_number: (auction) =>
    auction.patient.profile?.financing?.insurance_number?.decrypted ?? "",
  living_lift: (auction) =>
    auction.patient.profile?.lift_available_state as PredicamentState,
  living_lift_description: (auction) =>
    auction.patient.profile?.lift_available_description ?? null,
  living_situation: (auction) => {
    const livingSituation = auction.patient.profile?.living_situation;

    switch (livingSituation) {
      case LIVING_SITUATION_ALONE:
        return HOME_CARE_SITUATION.HOME;
      case LIVING_SITUATION_WITH_FAMILY:
        return HOME_CARE_SITUATION.HOME;
      case LIVING_SITUATION_CAREHOME:
        return HOME_CARE_SITUATION.CARE_HOME;
      case LIVING_SITUATION_IN_ELDERLY_HOME:
      case LIVING_SITUATION_SHARED_FLAT:
        return HOME_CARE_SITUATION.ASSISTED_LIVING;
      default:
        return null;
    }
  },
  living_situation_at_home: (auction) => {
    const livingSituation = auction.patient.profile?.living_situation;

    switch (livingSituation) {
      case LIVING_SITUATION_ALONE:
        return AT_HOME_SITUATION.ALONE;
      case LIVING_SITUATION_WITH_FAMILY:
        return AT_HOME_SITUATION.RELATIVES;
      default:
        return null;
    }
  },
  patient_birthdate: auctionBirthdatetoUnix,
  patient_city: (auction) => auction.profile?.search_location?.city,
  patient_first_name: (auction) =>
    auction?.patient?.profile?.first_name?.decrypted,
  patient_housenumber: (auction) =>
    auction.profile?.search_location?.encrypted_house_number?.decrypted,
  patient_last_name: (auction) =>
    auction?.patient?.profile?.last_name?.decrypted,
  patient_street: (auction) => auction.profile?.search_location?.street,
  patient_zipcode: (auction) => auction.profile?.search_location?.zipcode,
  site_id_number: (auction) =>
    auction.patient.careseeker?.site_id_number ?? null,
  social_worker_fax_number: (auction) =>
    auction.patient.social_worker?.fax_number ?? null,
  social_worker_first_name: (auction) =>
    auction.patient.social_worker?.first_name,
  social_worker_last_name: (auction) =>
    auction.patient.social_worker?.last_name,
  social_worker_phone_number: (auction) =>
    auction.patient.social_worker?.phone ?? null,

  ...createGuardianGetters(GUARDIAN_1),
  ...createGuardianGetters(GUARDIAN_2),
});
